import { cn } from '@flowstate/utils';

/**
 * Touch target size constants following accessibility guidelines
 * Minimum 44px x 44px for touch targets (WCAG 2.1 AA)
 */
export const TOUCH_TARGET_SIZES = {
  small: "min-h-[44px] min-w-[44px]",
  medium: "min-h-[48px] min-w-[48px]", 
  large: "min-h-[56px] min-w-[56px]",
  xl: "min-h-[64px] min-w-[64px]"
} as const;

/**
 * Represents the available touch target size options for UI elements.
 * - 'small': Minimum 44px x 44px, suitable for compact touch targets.
 * - 'medium': Minimum 48px x 48px, for standard buttons and controls.
 * - 'large': Minimum 56px x 56px, for prominent or primary actions.
 * - 'xl': Minimum 64px x 64px, for extra-large or highly accessible targets.
 */
export type TouchTargetSize = keyof typeof TOUCH_TARGET_SIZES;


/**
 * Touch-friendly spacing utilities
 */
export const TOUCH_SPACING = {
  tight: "gap-2", // 8px
  normal: "gap-3", // 12px
  comfortable: "gap-4", // 16px
  spacious: "gap-6" // 24px
} as const;

export type TouchSpacing = keyof typeof TOUCH_SPACING;

/**
 * Enhanced button classes for mobile touch targets
 */
export const getTouchButtonClasses = (
  size: TouchTargetSize = 'small',
  additionalClasses?: string
) => {
  return cn(
    TOUCH_TARGET_SIZES[size],
    "flex items-center justify-center",
    "touch-manipulation", // Optimize for touch
    "select-none", // Prevent text selection on touch
    "active:scale-95", // Visual feedback on touch
    "transition-transform duration-75", // Smooth touch feedback
    additionalClasses
  );
};

/**
 * Touch-friendly interactive area classes
 */
export const getTouchAreaClasses = (
  size: TouchTargetSize = 'small',
  spacing: TouchSpacing = 'normal',
  additionalClasses?: string
) => {
  return cn(
    TOUCH_TARGET_SIZES[size],
    TOUCH_SPACING[spacing],
    "touch-manipulation",
    "cursor-pointer",
    "select-none",
    additionalClasses
  );
};

/**
 * Mobile-optimized form input classes
 */
export const getTouchInputClasses = (additionalClasses?: string) => {
  return cn(
    "min-h-[48px]", // Larger than default for easier touch
    "px-4 py-3", // More generous padding
    "text-base", // Prevent zoom on iOS
    "touch-manipulation",
    additionalClasses
  );
};

/**
 * Utility to add touch-friendly padding around clickable elements
 */
export const addTouchPadding = (element: string, padding: 'sm' | 'md' | 'lg' = 'md') => {
  const paddingMap = {
    sm: 'p-2',
    md: 'p-3', 
    lg: 'p-4'
  };
  
  return cn(element, paddingMap[padding]);
};

/**
 * Check if an element meets minimum touch target requirements
 */
export const validateTouchTarget = (element: HTMLElement): boolean => {
  const rect = element.getBoundingClientRect();
  return rect.width >= 44 && rect.height >= 44;
};

/**
 * Mobile-first responsive touch classes
 */
export const RESPONSIVE_TOUCH = {
  // Larger touch targets on mobile, smaller on desktop
  adaptive: "min-h-[48px] min-w-[48px] md:min-h-[40px] md:min-w-[40px]",
  // Mobile-only larger targets
  mobileOnly: "min-h-[48px] min-w-[48px] md:min-h-auto md:min-w-auto",
  // Always large for accessibility
  accessible: "min-h-[48px] min-w-[48px]"
} as const;

/**
 * Touch gesture hint classes for visual feedback
 */
export const TOUCH_HINTS = {
  swipeable: "relative after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-8 after:h-1 after:bg-muted-foreground/30 after:rounded-full",
  draggable: "cursor-grab active:cursor-grabbing",
  longPressable: "relative after:absolute after:top-1 after:right-1 after:w-2 after:h-2 after:bg-primary/40 after:rounded-full after:animate-pulse"
} as const;
